import React from 'react'
import { InferType } from 'yup'
import { useMutation } from '@tanstack/react-query'
import { useAppTranslation } from '../../../../hooks/useAppTranslation'
import { Modal } from '../../../common/Modal'
import { ModalHeader } from '../../../common/Modal/ModalHeader'
import { theme } from '../../../../theme/theme'
import { ModalBody } from '../../../common/Modal/ModalBody'
import { useToastNotifications } from '../../../../hooks/useToastNotification'
import { ToastType } from '../../../common/Toast/toast-type'
import { useCurrentSociety } from '../../../../hooks/useCurrentSociety'
import { ResidentUserType } from '../../../../state/stores/units'
import { InviteForm } from '../InviteForm'
import { societyPreApprovedCreateSchema } from '../../../../forms/schemas/society_pre_approved_create'
import { createUserRole } from '../../../../api/societies'
import { EUnitRole } from '../../../../interfaces/models/units.interfaces'
import { BoardRole } from '../../../../types/board-roles'
import {
  ESocietyAdminRole,
  ESocietyBoardRole,
} from '../../../../interfaces/models/societies.interfaces'
import { i18nSocietyType } from '../../../../i18n'

interface InviteModalProps {
  show: boolean
  close: () => void
  residentType: ResidentUserType
  children?: React.ReactNode
}

export const InviteModal = ({
  show,
  close,
  residentType,
  children,
}: InviteModalProps): React.ReactNode => {
  const { translate } = useAppTranslation()
  const { society, isLoading: isLoadingSociety } = useCurrentSociety()
  const { setToastNotification } = useToastNotifications()

  const showSuccessMessage = (): void => {
    setToastNotification(
      ToastType.SUCCESS,
      translate('registerInvite.flashMessage.inviteSentSuccess')
    )
  }

  const showErrorMessage = (): void => {
    setToastNotification(
      ToastType.SUCCESS,
      translate('registerInvite.flashMessage.inviteSentFailure')
    )
  }

  const { mutate: mutateCreateUserRole, isPending: isCreatingUserRole } =
    useMutation({
      mutationFn: createUserRole,
      onSuccess: () => {
        showSuccessMessage()
        close()
      },
      onError: () => {
        showErrorMessage()
      },
    })

  const onSubmit = async (
    data: InferType<typeof societyPreApprovedCreateSchema>
  ): Promise<void> => {
    mutateCreateUserRole({
      sendNotifications: true,
      body: [
        {
          userEmail: data.userEmail.toLowerCase(),
          societyId: data.societyId,
          unitRole: data.unitRole as EUnitRole,
          unitIdOrTitle: data.unitIdOrTitle,
          ...(data.inviteAsAdmin && {
            adminRole: ESocietyAdminRole.None,
          }),
          ...(data.boardRole === BoardRole.NOMINATING_COMMITTEE
            ? { nominatingCommitteeRole: true }
            : { boardRole: data.boardRole as ESocietyBoardRole }),
        },
      ],
    })
  }

  const onError = (): void => {
    setToastNotification(
      ToastType.DANGER,
      translate('flashMessage.somethingWentWrongError')
    )
  }

  const loading = isLoadingSociety || isCreatingUserRole

  return (
    <>
      {children}
      <Modal show={show}>
        {{
          header: (
            <ModalHeader onClose={close}>
              <div>
                <p style={theme.textVariants.h3}>
                  {translate(
                    residentType === ResidentUserType.MEMBER
                      ? 'registerInvite.modal.inviteMember'
                      : 'registerInvite.modal.inviteNonMember',
                    {
                      context: i18nSocietyType(society?.societyTypeDisplayName),
                    }
                  )}
                </p>
              </div>
            </ModalHeader>
          ),
          body: (
            <ModalBody>
              {society && (
                <InviteForm
                  society={society}
                  onError={onError}
                  onSubmit={onSubmit}
                  onClose={close}
                  loading={loading}
                  residentType={residentType}
                />
              )}
            </ModalBody>
          ),
        }}
      </Modal>
    </>
  )
}
